import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import DraggableGrid from '../DraggableGrid';
import SortablePage from './SortablePage';
import type { BoardPage, PageBoardSource } from './pageBoard';

// Bundled locally, matching PDFViewer. The pdfjs-dist pin has to stay in
// lockstep with react-pdf's requirement or the API and worker versions diverge
// at runtime.
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PageEditorProps {
  sources: PageBoardSource[];
  pages: BoardPage[];
  onChange: (pages: BoardPage[]) => void;
  onSourceLoaded: (index: number, pageCount: number) => void;
  disabled?: boolean;
  /** Keys of the pages ticked to be split out. */
  splitKeys?: Set<string>;
  /** Tick or untick a page. Omitted where splitting is not offered. */
  onToggleSplit?: (key: string) => void;
}

/** The narrowest a page is drawn, so a phone still shows something legible. */
const MIN_PREVIEW_WIDTH = 240;

/**
 * The pages of a fax, as a rail you work in and a page you read.
 *
 * The grid this replaces could show a whole fax at a glance and nothing in it
 * legibly. A page of a fax is a referral somebody has to read before deciding
 * where it goes, and a ninety pixel thumbnail is not readable, so the work of
 * reordering moved to the rail and the page itself got the room.
 *
 * Whatever is on the rail, in the order shown, is the document that gets built.
 * Dropping a page is removing it, reordering is dragging, and a rotation is a
 * quarter turn added to however the page already sits.
 *
 * Each source is opened exactly once and its parsed document handed to every
 * tile that draws from it, and to the preview. Opening one per tile meant a
 * sixty page fax started sixty workers and downloaded the same object sixty
 * times, which is how a tab dies with somebody's work in it.
 */
export default function PageEditor({
  sources,
  pages,
  onChange,
  onSourceLoaded,
  disabled = false,
  splitKeys,
  onToggleSplit,
}: PageEditorProps) {
  const [proxies, setProxies] = useState<Record<number, PDFDocumentProxy>>({});
  const [failed, setFailed] = useState<Set<number>>(new Set());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [paneWidth, setPaneWidth] = useState(0);

  // The page fills the pane it is in, which on the review page is most of
  // the screen, rather than a fixed width chosen for a dialog.
  const measure = useCallback(() => {
    const el = previewRef.current;
    if (el) setPaneWidth(el.clientWidth);
  }, []);

  const hasPages = pages.length > 0;
  useEffect(() => {
    measure();
    const el = previewRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, hasPages]);

  const previewWidth = Math.max(MIN_PREVIEW_WIDTH, paneWidth - 24);

  // Follows the rail rather than being set once. A page can be removed while it
  // is the one being read, and a preview left pointing at it would go blank
  // with no way back.
  const selected =
    pages.find(page => page.key === selectedKey) ?? pages[0] ?? null;

  useEffect(() => {
    if (selected && selected.key !== selectedKey) setSelectedKey(selected.key);
  }, [selected, selectedKey]);

  const rotate = useCallback(
    (key: string) => {
      onChange(
        pages.map(page =>
          page.key === key
            ? { ...page, rotation: (page.rotation + 90) % 360 }
            : page
        )
      );
    },
    [onChange, pages]
  );

  const remove = useCallback(
    (key: string) => {
      // Unticked as it goes, or a page that is no longer on the rail still
      // counts towards the split and the two halves disagree about what is
      // being separated.
      if (splitKeys?.has(key)) onToggleSplit?.(key);
      onChange(pages.filter(page => page.key !== key));
    },
    [onChange, onToggleSplit, pages, splitKeys]
  );

  const onLoad = useCallback(
    (index: number, pdf: PDFDocumentProxy) => {
      setProxies(current => ({ ...current, [index]: pdf }));
      onSourceLoaded(index, pdf.numPages);
    },
    [onSourceLoaded]
  );

  const onFail = useCallback(
    (index: number) => {
      setFailed(current => new Set(current).add(index));
      // Reported as zero pages rather than left unknown, so the board can still
      // be built from the sources that did open. One corrupt attachment used to
      // leave the dialog empty with no way forward.
      onSourceLoaded(index, 0);
    },
    [onSourceLoaded]
  );

  const selectedPdf = selected ? (proxies[selected.source] ?? null) : null;

  return (
    <div className='space-y-3'>
      {/* Mounted but not shown: this is what opens each PDF once. The rail and
          the preview both draw from the parsed document it yields. */}
      <div className='hidden'>
        {sources.map((source, index) => (
          <Document
            key={source.url}
            file={source.url}
            onLoadSuccess={pdf => onLoad(index, pdf)}
            onLoadError={() => onFail(index)}
          />
        ))}
      </div>

      {failed.size > 0 && (
        <p className='text-sm text-destructive'>
          {failed.size === sources.length
            ? 'This fax could not be read, so its pages cannot be shown.'
            : 'One of these faxes could not be read, so its pages are not shown.'}
        </p>
      )}

      {pages.length === 0 ? (
        <p className='py-8 text-center text-sm text-muted-foreground'>
          No pages left. A document needs at least one.
        </p>
      ) : (
        // Stacked on a phone, because a rail beside a preview leaves neither
        // usable at that width.
        <div className='flex flex-col gap-4 md:flex-row'>
          <div
            className='max-h-[60vh] w-full shrink-0 overflow-y-auto pr-1 md:max-h-[calc(100vh-14rem)] md:w-48'
            aria-label='Pages'
          >
            <DraggableGrid
              items={pages}
              onReorder={onChange}
              getItemId={page => page.key}
              sortingStrategy='vertical'
              gridClassName='grid grid-cols-3 gap-3 md:grid-cols-1'
              renderItem={(page, index) => (
                <SortablePage
                  // DraggableGrid maps without keying, so the caller supplies
                  // it. Without one React reuses tiles by position, which is
                  // exactly wrong for a rail whose purpose is reordering.
                  key={page.key}
                  page={page}
                  position={index + 1}
                  pdf={proxies[page.source] ?? null}
                  disabled={disabled}
                  selected={page.key === selected?.key}
                  onSelect={() => setSelectedKey(page.key)}
                  checked={splitKeys?.has(page.key) ?? false}
                  onCheckedChange={
                    onToggleSplit ? () => onToggleSplit(page.key) : undefined
                  }
                  onRotate={() => rotate(page.key)}
                  onRemove={() => remove(page.key)}
                />
              )}
            />
          </div>

          <div
            ref={previewRef}
            className='flex max-h-[70vh] min-h-[320px] min-w-0 flex-1 items-start justify-center overflow-auto rounded border bg-muted/30 p-3 md:max-h-[calc(100vh-14rem)]'
          >
            {selected && selectedPdf ? (
              // The rotation is a CSS transform rather than react-pdf's
              // `rotate` prop, for the same reason the rail does it: that prop
              // REPLACES the page's own angle while the server ADDS to it, so
              // a fax that arrived turned would preview upright and file on
              // its side.
              <div
                style={{ transform: `rotate(${selected.rotation}deg)` }}
                className='transition-transform'
              >
                <Page
                  pdf={selectedPdf}
                  pageNumber={selected.page}
                  width={previewWidth}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  loading={
                    <div className='h-[70vh] w-full animate-pulse rounded bg-muted' />
                  }
                  error={
                    <div className='flex h-[70vh] w-full items-center justify-center rounded bg-muted text-sm'>
                      This page could not be shown.
                    </div>
                  }
                />
              </div>
            ) : (
              <div className='h-[70vh] w-full animate-pulse rounded bg-muted' />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
